<template>
    <li class="a-accordion-item" :class="{ active: isActive }">
        <component
            :is="titleTag"
            v-if="$slots.title"
            :id="titleId"
            class="title"
            role="button"
            :tabindex="0"
            :aria-expanded="isActive"
            :aria-controls="$slots.default ? contentId : undefined"
            @click="onClick"
            @keydown.enter.space.prevent="onClick"
        >
            <slot name="title" />
        </component>
        <component
            :is="titleTag"
            v-else-if="isHeadingTag"
            class="title-heading"
        >
            <button
                :id="titleId"
                class="title"
                type="button"
                :aria-expanded="isActive"
                :aria-controls="$slots.default ? contentId : undefined"
                @click="onClick"
            >
                <a-glyph class="icon" :name="glyph" />
                {{ title }}
            </button>
        </component>
        <button
            v-else
            :id="titleId"
            class="title"
            type="button"
            :aria-expanded="isActive"
            :aria-controls="$slots.default ? contentId : undefined"
            @click="onClick"
        >
            <a-glyph v-if="title" class="icon" :name="glyph" />
            {{ title }}
        </button>
        <div
            v-if="$slots.default"
            :id="contentId"
            ref="content"
            class="content"
        >
            <div
                ref="inner"
                class="content-wrapper"
                :aria-hidden="!isActive"
                :inert="!isActive"
            >
                <slot />
            </div>
        </div>
    </li>
</template>

<script setup lang="ts">
import {
    ref,
    inject,
    onMounted,
    onBeforeUnmount,
    useId,
    computed,
    watch,
} from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import { getElementPath } from '@utils/analytics';
import { ACCORDION_CONTEXT_KEY } from './context.ts';

defineOptions({ name: 'AAccordionItem' });

const props = defineProps({
    glyph: { type: String, default: 'icon-pointer-right' },
    title: { type: String, default: '' },
    titleTag: { type: String, default: 'div' },
    event: { type: Object, default: null },
    active: Boolean,
});

const emit = defineEmits<{
    (event: 'title-click', value: MouseEvent): void;
}>();

const isActive = ref(props.active);
const content = ref<HTMLElement | null>(null);
const accordionItemId = useId();
const titleId = `${accordionItemId}-title`;
const contentId = `${accordionItemId}-content`;
const headingTags = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const isHeadingTag = computed(() => headingTags.has(props.titleTag));

const parent = inject(ACCORDION_CONTEXT_KEY, null);

let observer: MutationObserver | null = null;
let unregister: (() => void) | null = null;
let isMonitoring = false;

const changeHeight = () => {
    const el = content.value;
    if (!el) return;
    el.style.height = `${el.scrollHeight}px`;
};

const changeHeightIfActive = () => {
    if (isActive.value) changeHeight();
};

const startMonitoring = () => {
    const el = content.value;
    if (!el || isMonitoring) return;

    isMonitoring = true;
    window.addEventListener('resize', changeHeightIfActive);

    observer ??= new MutationObserver((mutations) => {
        if (mutations.some((x) => x.type === 'childList')) {
            changeHeightIfActive();
        }
    });
    observer.observe(el, {
        childList: true,
        subtree: true,
    });
};

const stopMonitoring = () => {
    if (!isMonitoring) return;

    isMonitoring = false;
    window.removeEventListener('resize', changeHeightIfActive);
    observer?.disconnect();
    observer = null;
};

const setActive = (state: boolean) => {
    isActive.value = state;

    if (state) {
        changeHeight();
        startMonitoring();
    } else {
        stopMonitoring();
    }
};

const onClick = (event: any) => {
    const willOpen = !isActive.value;
    parent?.handleAllActives?.();
    setActive(willOpen);
    emit('title-click', event);
    parent?.emitActiveChanged?.(isActive.value);
    parent?.emitChangeEvent?.();

    if (!props.event) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'Acronis',
        eventCategory: 'Interactions',
        eventContext: willOpen ? 'show' : 'hide',
        eventLocation: getElementPath(event.target),
        ...props.event,
    });
};

watch(
    () => props.active,
    (active) => {
        if (active === isActive.value) return;
        if (active) parent?.handleAllActives();
        setActive(active);
    },
);

onMounted(() => {
    unregister = parent?.registerAccordionItem({
        isActive: () => isActive.value,
        removeActive: () => setActive(false),
    }) ?? null;

    setActive(props.active);
});

onBeforeUnmount(() => {
    stopMonitoring();
    unregister?.();
});
</script>

<style lang="postcss" scoped>
.a-accordion-item {
    border-top: var(--av-border);

    &:not(.active) {
        .content {
            height: 0 !important;
        }
    }

    .title {
        width: 100%;
        border: 0;
        background: none;
        font: inherit;
        text-align: start;
        cursor: pointer;
        display: flex;
        color: var(--av-nav-primary);
        transition: color ease-out 0.3s;
        padding: 16px 40px 16px 16px;

        &:hover {
            color: var(--av-nav-secondary);

            :deep(.icon) {
                fill: var(--av-nav-secondary);
            }
        }
    }

    .title-heading {
        margin: 0;
    }

    .content {
        overflow: hidden;
        max-height: max-content;
        font-size: 14px;
        line-height: 20px;
        transition: height 0.2s ease-in-out;
    }

    .content-wrapper {
        padding: 8px 40px 16px;
    }

    :deep(.icon) {
        margin-top: 4px;
        transition: fill ease-out 0.3s, transform 0.25s ease-out;
        flex-shrink: 0;
        fill: var(--av-brand-secondary);
        margin-inline-end: 8px;
    }

    &.active :deep(.icon) {
        transform: rotate(90deg);
    }
}

[dir="rtl"] {
    .a-accordion-item :deep(.icon) {
        transform: rotate(180deg);
    }
    .a-accordion-item.active :deep(.icon) {
        transform: rotate(90deg);
    }
}
</style>
